import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Episode, EpisodeTranslationSummary } from '../../../types';
import { buildContentTabs, ContentTabDef } from './episodeTabs';

/** 浏览位置记忆：当前 tab + 各 tab 的滚动位置。由父层持有（按集重建），面板关闭重开时恢复。 */
export interface ScriptPeekMemory {
  tab?: string;
  scroll: Record<string, number>;
}

interface EpisodeScriptPeekProps {
  episode: Episode;
  /** 代词标注版内容（页面加载时已预载）。 */
  content: string;
  originalContent: string;
  originalContentLoaded: boolean;
  normalizedContent: string;
  normalizedContentLoaded: boolean;
  translations: EpisodeTranslationSummary[];
  transContent: Record<string, string>;
  onLoadOriginal: () => void;
  onLoadNormalized: () => void;
  onLoadTranslation: (language: string) => void;
  /** 不传则不记忆（每次打开回到默认 tab、顶部）。 */
  memory?: ScriptPeekMemory;
}

/**
 * 「本集梗概」浮泡小窗里的只读剧本视图：默认展示改写版，可切换其他版本。
 * 内容态与底部「剧集原文」面板共享（懒加载回调也是同一套），自身只管 tab 选择与滚动记忆。
 */
export default function EpisodeScriptPeek({
  episode,
  content,
  originalContent,
  originalContentLoaded,
  normalizedContent,
  normalizedContentLoaded,
  translations,
  transContent,
  onLoadOriginal,
  onLoadNormalized,
  onLoadTranslation,
  memory,
}: EpisodeScriptPeekProps) {
  const { t } = useTranslation('episodeDetail');
  // 默认展示改写版；没有改写版则回落：代词标注版 > 原文。有记忆则恢复上次 tab。
  const defaultTab = episode.normalizedContentURL ? 'normalized' : episode.annotatedContentURL ? 'annotated' : 'original';
  const [tab, setTabState] = useState<string>(memory?.tab ?? defaultTab);
  const setTab = (key: string) => {
    setTabState(key);
    if (memory) memory.tab = key;
  };

  // 切换分集时回到默认 tab（父层按集换 memory/key 时此处兜底）
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setTabState(memory?.tab ?? defaultTab); }, [episode.id]);

  // 当前 tab 的内容未加载则懒加载（回调幂等，已加载会直接返回）
  useEffect(() => {
    if (tab === 'normalized') onLoadNormalized();
    else if (tab === 'original') onLoadOriginal();
    else if (tab.startsWith('trans:')) onLoadTranslation(tab.slice('trans:'.length));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, episode.id]);

  const tabs: ContentTabDef[] = buildContentTabs(episode, translations, t);
  const activeTrans = tabs.find(x => x.key === tab && x.kind === 'translation');

  // 文本（可滚动 pre）与状态提示（翻译中/失败/加载中）分开算，便于统一挂滚动记忆。
  let displayText: string | null = null;
  let statusNode: React.ReactNode = null;
  if (activeTrans) {
    const lang = activeTrans.language as string;
    if (activeTrans.status === 'translating') statusNode = <p style={hintStyle}>{t('content.translating')}</p>;
    else if (activeTrans.status === 'failed') statusNode = <p style={{ ...hintStyle, color: 'var(--danger)' }}>{t('content.translateFailed')}</p>;
    else if (transContent[lang] !== undefined) displayText = transContent[lang];
    else statusNode = <p style={hintStyle}>{t('loading')}</p>;
  } else {
    displayText = tab === 'normalized'
      ? (normalizedContentLoaded ? normalizedContent : null)
      : tab === 'annotated'
        ? (content || null)
        : (originalContentLoaded ? originalContent : null);
    if (displayText === null) statusNode = <p style={hintStyle}>{t('loading')}</p>;
  }

  // 滚动记忆：内容渲染后恢复该 tab 上次的位置（挂载/切 tab/内容异步就绪时都会执行）。
  const preRef = useRef<HTMLPreElement>(null);
  const hasText = displayText !== null;
  useLayoutEffect(() => {
    if (preRef.current) preRef.current.scrollTop = memory?.scroll[tab] ?? 0;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, hasText]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', flex: 1, minHeight: 0 }}>
      {tabs.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', background: 'var(--surface-2)', borderRadius: 'var(--radius-sm)', padding: '2px', alignSelf: 'flex-start', flexShrink: 0 }}>
          {tabs.map(tabDef => (
            <button
              key={tabDef.key}
              onClick={() => setTab(tabDef.key)}
              style={{
                padding: '3px 12px', fontSize: '0.78rem', borderRadius: '4px', border: 'none',
                cursor: 'pointer', fontWeight: tab === tabDef.key ? 600 : 400,
                background: tab === tabDef.key ? 'var(--surface)' : 'transparent',
                color: tab === tabDef.key ? 'var(--primary)' : 'var(--text-3)',
                boxShadow: tab === tabDef.key ? 'var(--shadow-xs)' : 'none',
                transition: 'all var(--transition-fast)',
              }}
            >
              {tabDef.label}
            </button>
          ))}
        </div>
      )}
      {hasText ? (
        <pre
          ref={preRef}
          onScroll={(e) => { if (memory) memory.scroll[tab] = e.currentTarget.scrollTop; }}
          style={preStyle}
        >{displayText}</pre>
      ) : statusNode}
    </div>
  );
}

const hintStyle: React.CSSProperties = { color: 'var(--text-3)', margin: 0 };

const preStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit',
  fontSize: '0.85rem', color: 'var(--text-2)', margin: 0, lineHeight: 1.7,
  overflowY: 'auto', flex: 1, minHeight: 0,
};
